<template>
    <div class="_accountPersonPopupModal">
      <BasicForm :update="updateForm" :saveFunc="save" :cancelFunc="cancelFunc" :form="form" :$v="$v" :formConfig="formConfig" :recordData="recordData" :headerColor="headerColor">
      </BasicForm>
      <div class="md-autocomplete-loading" v-show="isPromisePending">
        <md-progress-spinner :md-diameter="40" :md-stroke="4" md-mode="indeterminate" />
      </div>
      <CrudToolbar :save="save" :form="form" :$v="$v" :formConfig="formConfig" :cancel="cancelFunc"></CrudToolbar>
    </div>
</template>

<script>
  import _ from 'lodash';
  import Record from '@/mixins/Record';
  import BasicForm from '@/components/DynamicForm/BasicForm';
  import CrudToolbar from '@/components/Crud/Toolbar';

  // Form validations
  import { required, numeric, minLength, email } from "vuelidate/lib/validators";
  import {API, graphqlOperation} from "aws-amplify";
  import * as queries from '@/graphql/queries';
  import * as queries_custom from '@/graphql/queries_custom';
  import * as mutations from '@/graphql/mutations';
  
  import PersonComponent, { personFields, personForm, personValidations } from '../People/Person';
  const personMixin = {
	methods: {
	  getRegionEnumOptions: PersonComponent.methods.getRegionEnumOptions,
	  getCountryEnumOptions: PersonComponent.methods.getCountryEnumOptions,
	},
  };

  export default {
    name: "AccountPersonPopupModal",
    mixins: [Record, personMixin],
    components: {
      BasicForm,
      CrudToolbar
    },
    props: {
      relateRecordInput: {
        type: Object,
        required: true
      },
      list: {
        type: Object,
        required: true
      },
      relateRecordInputField: {
        type: String,
        required: true
	  },
	  createNew: {
        type: Boolean,
        required: true
	  },
	  atAddNewPerson: {
		type: Function,
		required: true,
	  },
    },

    data() {
		return {
			roleOptions: [],
			regionOptions: [],
			countryOptions: [],
			isPromisePending: false,
			gql: {
				queries: queries,
				queries_custom: queries_custom,
				mutations: mutations,
			},
			formConfig: {
				actions: {
					edit: 'editAccount',
				},
				slots: {
					basic: {label: 'Basic Information', icon: 'work', layout: {"x":0,"y":0,"w":12,"h":18,"i":"0"}, isLabelEnabled: this.createNew },
					contact: {label: 'Contact Information', icon: 'work', layout: {"x":0,"y":12,"w":12,"h":18,"i":"1"}, isLabelEnabled: this.createNew },
					additional: {label: 'Additional Information', icon: 'work', layout: {"x":0,"y":24,"w":12,"h":18,"i":"2"}, isLabelEnabled: this.createNew },
				},
				headerIcon: 'devices',
				objectName: 'Account',
				objectNamePlural: 'Accounts',
				editMode: false,
				pageMode: 'new', // can be one of ['new', 'view' 'edit']
				fields: {
					...{
						role: {
							label: "Role",
							type: "select",
							slot: 'basic', options: [], optionType: 'enum',
						},
						title: {
							label: "Title",
							type: "text",
							slot: 'basic',
						},
						email: {
							label: "Email",
							type: "text",
							slot: 'basic',
						},
						business_email: {
							label: "Business Email",
							type: "text",
							slot: 'basic',
						},
						other_email: {
							label: "Other Email",
							type: "text",
							slot: 'basic',
						},
						ownership: {
							label: "Ownership(%)",
							type: "text",
							slot: 'basic',
						},
					},
					...this.getPersonFields(),
				},
			},
			form: {
				..._.cloneDeep(personForm),
				...{
					role: '',
					title: '',
					email: '',
					business_email: '',
					other_email: '',
					ownership: '',
				}
			},
		}
	},
    validations() {
		return {
			form: {
				..._.cloneDeep(personValidations),
				... {
					given_name: this.createNew ? {required, minLength: minLength(3)} : {},
					family_name: this.createNew ? {required, minLength: minLength(3)} : {},
					role: {required},
					title: {},
					ownership: {numeric},
					email: this.createNew ? {required, email} : {email},
					business_email: {email},
					other_email: {email},
				}
			},
      	}
	},
	mounted() {
		API.graphql(graphqlOperation(queries_custom['newPerson']))
			.then(response => {
				this.getRegionEnumOptions(response.data);
				this.getCountryEnumOptions(response.data);
			})
			.catch(err => {
				// 
			})
	},
    methods: {
		getPersonFields() {
			const fields = _.cloneDeep(personFields);
			delete fields.email;
			Object.keys(fields).map(_key => {
				fields[_key].hide = () => !this.createNew;
			});
			return fields;
		},
      	updateFormAfter(data) {
			if (typeof data === 'undefined') return;
			// Configure the Role field
			(($this) => {
				this.roleOptions = [];
				if (data.listPersonAccountRoleEnum && data.listPersonAccountRoleEnum.enumValues) {
					data.listPersonAccountRoleEnum.enumValues.map(_value => {
						this.roleOptions.push({
							id: _value.name,
							name: _value.name.replace(/\_/g, ' '),
						});
					});
				}
				$this.options = this.roleOptions;
			})(this.formConfig.fields.role);

			// Pull business email from service and set in business email field  
			(($this) => {
				if (data.getAccount && data.getAccount.services && data.getAccount.services.items && data.getAccount.services.items.length) {
					let service = data.getAccount.services.items[0];
					$this.business_email = service && service.email ? service.email : "";
				}
			})(this.form);

			// Pull email from person list and set in email field  
			(($this) => {
				if (this.list && this.list.selectedItem && this.list.selectedItem.email) {
					$this.email = this.list.selectedItem.email;
				}
			})(this.form);
		},
		cancelFunc() {
			this.isPromisePending = false;
			this.list.showPopupModal = false;
			this.list.createNew = false;
		},
		
		async save() {
			this.isPromisePending = true;
			this.$v.$touch();
			if(this.$v.form.$invalid) {
				this.$c.log('save(): unable to save, $v.invalid');
				this.isPromisePending = false;
				return;
			}

			let data = {};
			const form = this.form;
			const fields = this.formConfig.fields;

			this.$loopo(fields, (field, name) => {
				if (!field.fields) {
					if (typeof form[name] !== 'undefined' && form[name] !== null) {
						if (field.type === 'datepicker') {
							try{
								data[name] = form[name].toUTCString();
							}catch (e) {
								data[name] = form[name].toString();
							}
						} else {
							data[name] = form[name];
						}
					}
				} else {
					if (field.type === 'json') {
						const jsonFields = form[name].fields;
						Object.keys(jsonFields).map(_key => {
							if (field.fields && field.fields[_key] && field.fields[_key].removeIfEmpty) {
								if (!jsonFields[_key] || !jsonFields[_key].toString().length) {
									delete(jsonFields[_key]);
								}
							}
						});
						data[name] = JSON.stringify(jsonFields);
					}
				}
			});

			// We should not send any empty strings on create
			if (this.formConfig.pageMode == 'new') {
				this.$loopo(fields, (field, name) => {
					if (!field.fields) {
						if (data[name] === "") {
							delete data[name];
						}
						// remove the form field which should not actually be stored in schema modal record.
						if (this.formConfig.removeField) {
							delete data[this.formConfig.removeField];
						}
					} else {
						if (field.type === 'json') {
							let isEmpty = true;
							this.$loopo(field.fields, (ffield, fname) => {
								let formValue = form[name].fields[fname];
								if (formValue !== '') {
									isEmpty = false;
								}
							});
							if (isEmpty) {
								delete data[name];
							}
						}
					}
				});
			}

			if (this.formConfig.pageMode == 'edit') {
				this.$loopo(fields, (field, name) => {
					if (!field.fields) {
						if (data[name] === "" || data[name] === undefined) {
							data[name] = null;
						}
						// remove the form field which should not actually be stored in schema modal record.
						if (this.formConfig.removeField) {
							delete data[this.formConfig.removeField];
						}
					} else {
						if (field.type === 'json') {
							let isEmpty = true;
							this.$loopo(field.fields, (ffield, fname) => {
								let formValue = form[name].fields[fname];
								if (formValue !== '') {
									isEmpty = false;
								}
							});
							if (isEmpty) {
								data[name] = null;
							}
						}
					}
				});
			}

			if (this.formConfig.removeFields && this.formConfig.removeFields.length) {
				this.formConfig.removeFields.forEach(item => {
					delete data[item];
				});
			}

			this.$c.log('data has been prepared form=', data);
			await this.create(data);
			this.cancelFunc();
		},
			
		async create(data) {
			
			let input = {
				...this.relateRecordInput,
				...data,
			};

			if (this.list && this.list.selectedItem && this.list.selectedItem.id) {
				input = {
					...input,
					[this.relateRecordInputField]: this.list.selectedItem.id
				}
			}

			// ATLAS-1014 create new person record
			if(this.createNew){
				const personData = {};
				Object.keys(personForm).map(_key => {
					personData[_key] = input[_key];
				});
				await API.graphql(graphqlOperation(mutations['createPerson'], {input: personData}))
					.then(async response => {
						await this.createPersonRelation(input, response.data.createPerson.id);
						this.cancelFunc();
						setTimeout(() => {
							if (this.list && this.list.afterAdd) {
								this.list.afterAdd(this.list.selectedItem);
							}
						}, 3000);
					})
					.catch(error => {
						this.$c.log(error);
						this.$set(this.formConfig, 'formErrors', error);
					});				
			}else{
				this.createPersonRelation(input, this.list.selectedItem.id)
					.then(response => {
						// Calling afterAdd method of RecordList to remove the selected item from the list
						if (this.list && this.list.afterAdd)
							this.list.afterAdd(this.list.selectedItem);
					})
					.catch(error => {
						this.$c.log(error);
						this.$set(this.formConfig, 'formErrors', error);
					});
			}
		},
		/**
		 * Create Person Relation
		 * @param {Object} data - form data
		 * @param {String} personId - the Person ID
		 * @returns {Promise}
		 */
		createPersonRelation(data, personId) {
			const input  = {
				role: data.role,
				title: data.title,
				email: data.email,
				business_email: data.business_email,
				other_email: data.other_email,
				ownership: data.ownership,
				owner: data.owner,
				...this.relateRecordInput,
				[this.relateRecordInputField]: personId,
			};
			
			return new Promise((resolve, reject) => {
				API.graphql(graphqlOperation(mutations[this.list.createMutationId], { input }))
					.then((response) => {
						resolve(response);
					})
					.catch(error => {
						this.$c.log(error);
						this.$set(this.formConfig, 'formErrors', error);
						reject()
					});
			});
		},
		getMutation(name) {
			let result = false;
			if (name) {
				if (typeof this.gql.mutations[name] !== 'undefined') {
					result = this.gql.mutations[name];
				}
			}
			return result;
		},
	}
}
</script>

<style lang="scss">
  .md-tabs .md-tabs-navigation {
    padding-left: 15px;
    color: black;
    display: none;

    .md-tab-nav-button.md-button {
      color: black !important;

      &.active {
        color: black !important;
        border-bottom: 2px solid black;
      }
    }
  }

  .md-content .md-tabs-container .md-tab > .md-layout-item {
    padding-left: 0;
    padding-right: 0;
  }

  [name="giftCardProvider"] {
    margin-top: 16px;
  }

  .form-field-view.search-modal-header {
    font-weight: 500;
  }
  
  .md-dialog.__customize_person_account .md-field .md-input {
	width: 445px;
  }

  .md-dialog.__customize_person_account .md-toolbar .md-toolbar-row {
	padding-left: 0px;
    padding-right: 0px;
  }

  .md-dialog.__customize_person_account .md-toolbar {
	padding-left: 0px;
    padding-right: 0px;
  }


  .isEditing .form-field-view.search-modal-header {
    font-weight: normal;
  }
	
  ._accountPersonPopupModal {
    #basic,
    #contact,
    #additional {
      width: 100%;
    }
  }
  
  .md-dialog.__customize_person_account.md-dialog-fullscreen.md-theme-default{
	max-width: 550px;
  }  
  
  @media (min-width: 1350px) {
	.md-dialog.__customize_person_account.md-dialog-fullscreen.md-theme-default{
	  max-width: 1080px;
	}
	
	._accountPersonPopupModal fieldset,
	._accountPersonPopupModal ._uiKitInput {
	  float: left;
	  width: 47%;
	  margin-right: 30px;
	}
	
	._accountPersonPopupModal fieldset ._uiKitInput {
	  float: none;
	  width: 100%;
	  margin-right: 0;
	}
  }
</style>
